import { useState } from "react";
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { useAuthStore } from "@/store/authStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { User, Lock, Loader2, ShieldCheck, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

export default function ProfilePage() {
  const { user } = useAuthStore();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Mật khẩu xác nhận không khớp");
      return;
    }
    if (newPassword.length < 6) {
      setError("Mật khẩu mới phải có ít nhất 6 ký tự");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (auth.currentUser) {
        try {
           await updatePassword(auth.currentUser, newPassword);
           setSuccess("Đổi mật khẩu thành công!");
           setNewPassword("");
           setConfirmPassword("");
           setCurrentPassword("");
        } catch (err: any) {
           if (err.code === 'auth/requires-recent-login' || err.message?.includes('recent-login')) {
             if (!currentPassword) {
               setError("Phiên làm việc đã cũ. Vui lòng nhập mật khẩu hiện tại để xác thực.");
               setLoading(false);
               return;
             }
             const credential = EmailAuthProvider.credential(auth.currentUser.email!, currentPassword);
             await reauthenticateWithCredential(auth.currentUser, credential);
             await updatePassword(auth.currentUser, newPassword);
             setSuccess("Mật khẩu đã được cập nhật thành công!");
             setNewPassword("");
             setConfirmPassword("");
             setCurrentPassword("");
           } else {
             throw err;
           }
        }
      }
    } catch (err: any) {
      console.error("Password update error:", err);
      setError(err.message || "Không thể đổi mật khẩu. Vui lòng kiểm tra lại mật khẩu hiện tại.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-20 mt-4">
      <Helmet>
        <title>Hồ sơ cá nhân | Flashcard Platform</title>
      </Helmet>

      <Link 
        to="/dashboard" 
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary font-bold text-sm transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        QUAY LẠI DASHBOARD
      </Link>

      {/* Profile Header */}
      <div className="relative overflow-hidden rounded-[3rem] bg-zinc-900 p-8 md:p-12 text-white shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 blur-[100px] rounded-full" />
        <div className="relative flex flex-col md:flex-row items-center gap-8">
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-[2.5rem] bg-primary flex items-center justify-center text-4xl font-black shadow-2xl shadow-primary/40">
            {user.displayName?.[0] || user.email?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="text-center md:text-left space-y-2">
            <h1 className="text-3xl md:text-5xl font-black tracking-tight">{user.displayName || "Người dùng"}</h1>
            <p className="text-zinc-400 font-medium text-lg flex items-center justify-center md:justify-start gap-2">
              <User className="h-5 w-5" /> {user.email}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Account Info */}
        <Card className="lg:col-span-1 border-2 rounded-[2.5rem] shadow-xl overflow-hidden bg-background/50 backdrop-blur-sm">
          <CardHeader className="pt-8 pb-4 px-8 border-b-2 border-dashed">
            <CardTitle className="flex items-center gap-2 text-xl font-black uppercase tracking-tight italic">
              <ShieldCheck className="h-5 w-5 text-primary" /> Thông tin
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8 space-y-6">
            <div className="space-y-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">ID Người dùng</p>
              <p className="font-mono text-xs truncate bg-muted p-2 rounded-lg">{user.uid}</p>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Trạng thái</p>
              <div className="flex items-center gap-2">
                 <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                 <span className="font-bold text-sm">Đang hoạt động</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password Form */}
        <Card className="lg:col-span-2 border-2 rounded-[2.5rem] shadow-xl overflow-hidden bg-background/50 backdrop-blur-sm">
          <CardHeader className="pt-8 pb-4 px-8">
            <CardTitle className="text-2xl font-black tracking-tight italic uppercase">Đổi mật khẩu</CardTitle>
            <CardDescription className="font-medium text-base">
              Cập nhật mật khẩu để bảo vệ tài khoản của bạn.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleChangePassword} className="space-y-6">
              {error && (
                <div className="bg-destructive/10 border-2 border-destructive/20 text-destructive p-4 rounded-2xl flex items-center gap-3 font-bold text-sm">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-emerald-500/10 border-2 border-emerald-500/20 text-emerald-600 p-4 rounded-2xl flex items-center gap-3 font-bold text-sm">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                  {success}
                </div>
              )}

              <div className="space-y-2">
                <Label className="font-bold text-xs uppercase tracking-widest text-muted-foreground ml-1">Mật khẩu mới</Label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                  <Input 
                    type="password" 
                    placeholder="Tối thiểu 6 ký tự" 
                    className="pl-12 h-14 rounded-2xl border-2 focus-visible:ring-primary/20 text-lg transition-all"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-bold text-xs uppercase tracking-widest text-muted-foreground ml-1">Xác nhận mật khẩu mới</Label>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                  <Input 
                    type="password" 
                    placeholder="Nhập lại mật khẩu mới" 
                    className="pl-12 h-14 rounded-2xl border-2 focus-visible:ring-primary/20 text-lg transition-all"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required 
                  />
                </div>
              </div>

              <div className="pt-4 border-t-2 border-dashed border-muted">
                <div className="space-y-2">
                  <Label className="font-bold text-xs uppercase tracking-widest text-primary ml-1 flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" /> Mật khẩu hiện tại (Yêu cầu xác thực)
                  </Label>
                  <p className="text-xs text-muted-foreground font-medium ml-1 mb-2">
                    Firebase yêu cầu mật khẩu hiện tại nếu phiên đăng nhập của bạn đã lâu.
                  </p>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input 
                      type="password" 
                      placeholder="Nhập mật khẩu đang dùng" 
                      className="pl-12 h-14 rounded-2xl border-2 focus-visible:ring-primary/20 text-lg transition-all bg-muted/20"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 rounded-2xl text-lg font-bold shadow-xl shadow-primary/20 transition-all active:scale-[0.98]"
                disabled={loading}
              >
                {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : "Cập nhật mật khẩu"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
